// Hero6.js - Contains functions for creating an advanced Hero section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a complete Hero section with nested elements
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the hero is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The hero elements
 */
export const createHeroElements = (
  getHierarchyName,
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const heroSectionClass = 'hero6-section';
  const heroContainerClass = 'hero6-container';
  const heroLeftColClass = 'hero6-left-col';
  const heroRightColClass = 'hero6-right-col';
  const heroBadgeClass = 'hero6-badge';
  const heroBadgeIconClass = 'hero6-badge-icon';
  const heroBadgeTextClass = 'hero6-badge-text';
  const heroTitleClass = 'hero6-title';
  const heroTitleIconClass = 'hero6-title-icon';
  const heroSubtitleClass = 'hero6-subtitle';
  const heroButtonClass = 'hero6-button';
  const heroScrollDownClass = 'hero6-scroll-down';
  const heroImageWrapperClass = 'hero6-image-wrapper';
  const heroMainImageClass = 'hero6-main-image';
  const heroAbstractShape1Class = 'hero6-abstract-shape1';
  const heroAbstractShape2Class = 'hero6-abstract-shape2';
  const heroAbstractShape3Class = 'hero6-abstract-shape3';
  const heroAbstractShape4Class = 'hero6-abstract-shape4'; // Squiggly line
  const heroFloatingUIChat1Class = 'hero6-floating-ui-chat1';
  const heroFloatingUIChat2Class = 'hero6-floating-ui-chat2';
  const heroFloatingUIChat3Class = 'hero6-floating-ui-chat3';
  const heroFloatingUIChat4Class = 'hero6-floating-ui-chat4';
  const heroAvatarClass = 'hero6-avatar';


  // Desktop Styles (default)
  const heroSectionStyles = {
    position: 'relative',
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '40px',
    paddingRight: '40px',
    backgroundColor: '#ffffff',
    color: '#0B0F19', // Dark text
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    minHeight: '100vh', // To make scroll down visible if content is short
  };

  const heroContainerStyles = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    gap: '60px',
    position: 'relative',
    zIndex: '1'
  };

  const heroLeftColStyles = {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    maxWidth: '580px',
    position: 'relative',
    zIndex: '2',
    alignItems: 'flex-start' // Align items to the start for left column
  };

  const heroRightColStyles = {
    flex: '1',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    minHeight: '500px' // Adjusted height
  };

  const heroBadgeStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: '#E0F2F7', // Light aqua/cyan
    borderRadius: '16px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#0B0F19' // Darker text for badge
  };
  
  const heroBadgeIconStyles = {
    width: '16px',
    height: '16px',
    // Add fox icon SVG or image here later
  };

  const heroBadgeTextStyles = {
    // No specific styles needed if inherited
  };

  const heroTitleStyles = {
    margin: '0',
    fontSize: '60px', // Larger font
    fontWeight: '700', // Bold
    color: '#0B0F19', // Dark text
    lineHeight: '1.2',
    letterSpacing: '-0.02em',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };

  const heroTitleIconStyles = {
    width: '32px', // Adjust size as needed
    height: '32px',
    color: '#FFD700', // Gold/Yellow for lightning bolt
  };

  const heroSubtitleStyles = {
    margin: '0',
    fontSize: '20px',
    fontWeight: '400',
    color: '#525C6B', // Lighter gray text
    lineHeight: '1.6',
    maxWidth: '500px'
  };

  const heroButtonStyles = {
    padding: '18px 36px', // Larger padding
    backgroundColor: '#3B82F6', // Blue button
    color: '#ffffff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '18px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    boxShadow: '0 4px 14px 0 rgba(59, 130, 246, 0.39)'
  };

  const heroScrollDownStyles = {
    position: 'absolute',
    bottom: '-60px', // Adjust positioning
    left: '0',
    width: '30px',
    height: '30px',
    cursor: 'pointer',
    // Add arrow icon SVG later
  };

  const heroImageWrapperStyles = {
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  };

  const heroMainImageStyles = {
    maxWidth: '450px', // Adjust as needed
    maxHeight: '550px',
    borderRadius: '20px',
    objectFit: 'cover',
    zIndex: '2',
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
  };

  const heroAbstractShape1Styles = { // Pink/Purple/Red gradient shape
    position: 'absolute',
    width: '400px',
    height: '400px',
    background: 'linear-gradient(135deg, #FF8C94 0%, #C53364 50%, #A52A2A 100%)', // Example gradient
    borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%', // Organic shape
    opacity: '0.6',
    transform: 'rotate(30deg) translate(50px, -50px)',
    zIndex: '1',
    filter: 'blur(20px)'
  };

  const heroAbstractShape2Styles = { // Light green circle
    position: 'absolute',
    width: '80px',
    height: '80px',
    backgroundColor: '#A8E6CF', // Light green
    borderRadius: '50%',
    opacity: '0.8',
    top: '10%',
    left: '5%',
    zIndex: '0',
    filter: 'blur(5px)'
  };

  const heroAbstractShape3Styles = { // Light purple circle
    position: 'absolute',
    width: '120px',
    height: '120px',
    backgroundColor: '#D7BDE2', // Light purple
    borderRadius: '50%',
    opacity: '0.7',
    bottom: '15%',
    right: '10%',
    zIndex: '0',
    filter: 'blur(10px)'
  };
  
  const heroAbstractShape4Styles = { // Squiggly line
    position: 'absolute',
    width: '100px', // Placeholder for SVG
    height: '50px', // Placeholder for SVG
    // Add SVG for squiggly line later
    bottom: '5%',
    left: '20%',
    zIndex: '0',
    // stroke: '#0B0F19',
    // strokeWidth: '2px',
    // fill: 'none',
  };

  const floatingUIBaseStyles = {
    position: 'absolute',
    backgroundColor: '#ffffff',
    padding: '12px 18px',
    borderRadius: '12px',
    boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    zIndex: '3',
    animation: 'floatAnimation 6s ease-in-out infinite'
  };

  const heroFloatingUIChat1Styles = { // Top chat bubble
    ...floatingUIBaseStyles,
    top: '15%',
    left: '-80px', // Adjust to be to the left of the image
  };
  
  const heroAvatarStyles = {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    objectFit: 'cover',
    backgroundColor: '#E2E8F0' // Placeholder color
  };

  const heroFloatingUIChat2Styles = { // Mireya (Orange)
    ...floatingUIBaseStyles,
    padding: '10px 20px',
    backgroundColor: '#FFD180', // Orange
    color: '#0B0F19',
    fontWeight: '500',
    top: '40%',
    right: '-70px', // Adjust to be to the right
  };

  const heroFloatingUIChat3Styles = { // Bottom right with progress
    ...floatingUIBaseStyles,
    bottom: '20%',
    right: '-100px',
    width: '220px' // Wider for progress bar
  };
  
  const heroFloatingUIChat4Styles = { // Davin (Purple)
    ...floatingUIBaseStyles,
    padding: '10px 20px',
    backgroundColor: '#C3AED6', // Light Purple
    color: '#0B0F19',
    fontWeight: '500',
    bottom: '5%',
    left: '60%', 
  };
  
  // Keyframes for floating animation
  // In a real app, this would be handled by a CSS-in-JS solution or a global stylesheet.
  // const floatAnimationStyleTag = document.createElement('style');
  // floatAnimationStyleTag.textContent = `
  //   @keyframes floatAnimation {
  //     0%, 100% { transform: translateY(0px); }
  //     50% { transform: translateY(-15px); }
  //   }
  // `;
  // document.head.appendChild(floatAnimationStyleTag);


  // Tablet Styles
  const tabletHeroContainerStyles = { ...heroContainerStyles, gap: '40px', padding: '0 30px' };
  const tabletHeroTitleStyles = { ...heroTitleStyles, fontSize: '48px' };
  const tabletHeroSubtitleStyles = { ...heroSubtitleStyles, fontSize: '18px' };
  const tabletHeroMainImageStyles = { ...heroMainImageStyles, maxWidth: '380px' };

  // Mobile Styles
  const mobileHeroContainerStyles = { ...heroContainerStyles, flexDirection: 'column', gap: '50px', padding: '0 20px', textAlign: 'center' };
  const mobileHeroLeftColStyles = { ...heroLeftColStyles, alignItems: 'center', maxWidth: '100%' };
  const mobileHeroTitleStyles = { ...heroTitleStyles, fontSize: '36px' };
  const mobileHeroSubtitleStyles = { ...heroSubtitleStyles, fontSize: '16px', maxWidth: '100%' };
  const mobileHeroButtonStyles = { ...heroButtonStyles, padding: '16px 32px', fontSize: '16px', width: '100%', maxWidth: '300px' };
  const mobileHeroRightColStyles = { ...heroRightColStyles, minHeight: '350px' };
  const mobileHeroMainImageStyles = { ...heroMainImageStyles, maxWidth: '80%', maxHeight: '400px' };
  const mobileScrollDownStyles = { ...heroScrollDownStyles, bottom: '20px', left: '50%', transform: 'translateX(-50%)'};
  const mobileFloatingUIChat1Styles = {...heroFloatingUIChat1Styles, top: '5%', left: '5%'};
  const mobileFloatingUIChat2Styles = {...heroFloatingUIChat2Styles, top: 'auto', bottom:'15%', right: '5%'};
  const mobileFloatingUIChat3Styles = {...heroFloatingUIChat3Styles, bottom: '5%', right: 'auto', left: '5%', width: '180px'};
  const mobileFloatingUIChat4Styles = {...heroFloatingUIChat4Styles, display: 'none'}; // Hide one on mobile if too cluttered

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Ensure devices are initialized
    if (!newRegistry[DEVICES.DESKTOP]) newRegistry[DEVICES.DESKTOP] = new Map();
    if (!newRegistry[DEVICES.TABLET]) newRegistry[DEVICES.TABLET] = new Map();
    if (!newRegistry[DEVICES.MOBILE]) newRegistry[DEVICES.MOBILE] = new Map();

    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(heroSectionClass, heroSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(heroContainerClass, heroContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(heroLeftColClass, heroLeftColStyles);
    newRegistry[DEVICES.DESKTOP].set(heroRightColClass, heroRightColStyles);
    newRegistry[DEVICES.DESKTOP].set(heroBadgeClass, heroBadgeStyles);
    newRegistry[DEVICES.DESKTOP].set(heroBadgeIconClass, heroBadgeIconStyles);
    newRegistry[DEVICES.DESKTOP].set(heroBadgeTextClass, heroBadgeTextStyles);
    newRegistry[DEVICES.DESKTOP].set(heroTitleClass, heroTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(heroTitleIconClass, heroTitleIconStyles);
    newRegistry[DEVICES.DESKTOP].set(heroSubtitleClass, heroSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(heroButtonClass, heroButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(heroScrollDownClass, heroScrollDownStyles);
    newRegistry[DEVICES.DESKTOP].set(heroImageWrapperClass, heroImageWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(heroMainImageClass, heroMainImageStyles);
    newRegistry[DEVICES.DESKTOP].set(heroAbstractShape1Class, heroAbstractShape1Styles);
    newRegistry[DEVICES.DESKTOP].set(heroAbstractShape2Class, heroAbstractShape2Styles);
    newRegistry[DEVICES.DESKTOP].set(heroAbstractShape3Class, heroAbstractShape3Styles);
    newRegistry[DEVICES.DESKTOP].set(heroAbstractShape4Class, heroAbstractShape4Styles);
    newRegistry[DEVICES.DESKTOP].set(heroFloatingUIChat1Class, heroFloatingUIChat1Styles);
    newRegistry[DEVICES.DESKTOP].set(heroFloatingUIChat2Class, heroFloatingUIChat2Styles);
    newRegistry[DEVICES.DESKTOP].set(heroFloatingUIChat3Class, heroFloatingUIChat3Styles);
    newRegistry[DEVICES.DESKTOP].set(heroFloatingUIChat4Class, heroFloatingUIChat4Styles);
    newRegistry[DEVICES.DESKTOP].set(heroAvatarClass, heroAvatarStyles);


    // Tablet styles
    newRegistry[DEVICES.TABLET].set(heroContainerClass, tabletHeroContainerStyles);
    newRegistry[DEVICES.TABLET].set(heroTitleClass, tabletHeroTitleStyles);
    newRegistry[DEVICES.TABLET].set(heroSubtitleClass, tabletHeroSubtitleStyles);
    newRegistry[DEVICES.TABLET].set(heroMainImageClass, tabletHeroMainImageStyles);
    // Add more tablet-specific style overrides if needed for other elements

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(heroContainerClass, mobileHeroContainerStyles);
    newRegistry[DEVICES.MOBILE].set(heroLeftColClass, mobileHeroLeftColStyles);
    newRegistry[DEVICES.MOBILE].set(heroTitleClass, mobileHeroTitleStyles);
    newRegistry[DEVICES.MOBILE].set(heroSubtitleClass, mobileHeroSubtitleStyles);
    newRegistry[DEVICES.MOBILE].set(heroButtonClass, mobileHeroButtonStyles);
    newRegistry[DEVICES.MOBILE].set(heroRightColClass, mobileHeroRightColStyles);
    newRegistry[DEVICES.MOBILE].set(heroMainImageClass, mobileHeroMainImageStyles);
    newRegistry[DEVICES.MOBILE].set(heroScrollDownClass, mobileScrollDownStyles);
    newRegistry[DEVICES.MOBILE].set(heroFloatingUIChat1Class, mobileFloatingUIChat1Styles);
    newRegistry[DEVICES.MOBILE].set(heroFloatingUIChat2Class, mobileFloatingUIChat2Styles);
    newRegistry[DEVICES.MOBILE].set(heroFloatingUIChat3Class, mobileFloatingUIChat3Styles);
    newRegistry[DEVICES.MOBILE].set(heroFloatingUIChat4Class, mobileFloatingUIChat4Styles);


    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    heroSectionClass, heroContainerClass, heroLeftColClass, heroRightColClass,
    heroBadgeClass, heroBadgeIconClass, heroBadgeTextClass, heroTitleClass, heroTitleIconClass,
    heroSubtitleClass, heroButtonClass, heroScrollDownClass, heroImageWrapperClass,
    heroMainImageClass, heroAbstractShape1Class, heroAbstractShape2Class, heroAbstractShape3Class,
    heroAbstractShape4Class, heroFloatingUIChat1Class, heroFloatingUIChat2Class,
    heroFloatingUIChat3Class, heroFloatingUIChat4Class, heroAvatarClass
    // Add floatAnimation here or ensure it's globally available
  ]));

  // Generate IDs for all elements
  const sectionID = `hero6-${uuidv4()}`;
  const containerID = `hero6-container-${uuidv4()}`;
  const leftColID = `hero6-leftcol-${uuidv4()}`;
  const rightColID = `hero6-rightcol-${uuidv4()}`;
  const badgeID = `hero6-badge-${uuidv4()}`;
  const badgeIconID = `hero6-badgeicon-${uuidv4()}`;
  const badgeTextID = `hero6-badgetext-${uuidv4()}`;
  const titleID = `hero6-title-${uuidv4()}`;
  const titleIconID = `hero6-titleicon-${uuidv4()}`;
  const subtitleID = `hero6-subtitle-${uuidv4()}`;
  const buttonID = `hero6-button-${uuidv4()}`;
  const scrollDownID = `hero6-scrolldown-${uuidv4()}`;
  const imageWrapperID = `hero6-imgwrapper-${uuidv4()}`;
  const mainImageID = `hero6-mainimg-${uuidv4()}`;
  const abstractShape1ID = `hero6-shape1-${uuidv4()}`;
  const abstractShape2ID = `hero6-shape2-${uuidv4()}`;
  const abstractShape3ID = `hero6-shape3-${uuidv4()}`;
  const abstractShape4ID = `hero6-shape4-${uuidv4()}`;
  const chat1ID = `hero6-chat1-${uuidv4()}`;
  const chat1AvatarID = `hero6-chat1avatar-${uuidv4()}`;
  const chat1ContentID = `hero6-chat1content-${uuidv4()}`;
  const chat2ID = `hero6-chat2-${uuidv4()}`;
  const chat3ID = `hero6-chat3-${uuidv4()}`;
  const chat3AvatarID = `hero6-chat3avatar-${uuidv4()}`;
  const chat3ContentID = `hero6-chat3content-${uuidv4()}`;
  const chat4ID = `hero6-chat4-${uuidv4()}`;

  // Define IDs for children of chat1Content
  const chat1Line1ID = `hero6-chat1-line1-${uuidv4()}`;
  const chat1Line2ID = `hero6-chat1-line2-${uuidv4()}`;
  const chat1CallIconID = `hero6-chat1-callicon-${uuidv4()}`;

  // Define IDs for children of chat3Content
  const chat3CheckmarkID = `hero6-chat3-checkmark-${uuidv4()}`;
  const chat3ProgressWrapperID = `hero6-chat3-progresswrapper-${uuidv4()}`;
  const chat3ProgressFillID = `hero6-chat3-progressfill-${uuidv4()}`;

  const newElements = [
    // Hero Section
    {
      id: sectionID,
      builderId: `builder-${uuidv4()}`,
      type: 'section',
      name: 'Hero Section 6',
      parentId: null,
      className: heroSectionClass,
      styles: {}, // Styles applied via class
      children: [containerID, scrollDownID],
      text: '',
      attributes: { 'data-element': 'hero-section', 'data-component': 'section', 'data-section-type': 'hero', 'data-page-id': currentPageId }
    },
    // Container
    {
      id: containerID,
      builderId: `builder-${uuidv4()}`,
      type: 'div',
      name: 'Hero Container',
      parentId: sectionID,
      hierarchyName: getHierarchyName('div'),
      className: heroContainerClass,
      styles: {},
      children: [leftColID, rightColID],
      text: '',
      attributes: { 'data-element': 'hero-container' }
    },
    // Left Column
    {
      id: leftColID,
      builderId: `builder-${uuidv4()}`,
      type: 'div',
      name: 'Left Column',
      parentId: containerID,
      hierarchyName: getHierarchyName('div'),
      className: heroLeftColClass,
      styles: {},
      children: [badgeID, titleID, subtitleID, buttonID],
      text: '',
      attributes: { 'data-element': 'hero-left-column' }
    },
    // Badge "ADELFOX.DESIGN"
    {
      id: badgeID,
      builderId: `builder-${uuidv4()}`,
      type: 'div',
      name: 'Brand Badge',
      parentId: leftColID,
      hierarchyName: getHierarchyName('div'),
      className: heroBadgeClass,
      styles: {},
      children: [badgeIconID, badgeTextID],
      text: '',
      attributes: { 'data-element': 'hero-badge' }
    },
    {
      id: badgeIconID,
      builderId: `builder-${uuidv4()}`,
      type: 'span', // Could be an img if you have an SVG/image file
      name: 'Badge Icon',
      parentId: badgeID,
      hierarchyName: getHierarchyName('span'),
      className: heroBadgeIconClass,
      styles: { width: '18px', height: '18px', backgroundColor: '#FF8C94', borderRadius: '4px' }, // Placeholder fox icon
      text: '', // SVG code for fox or leave empty for CSS background
      attributes: { 'data-element': 'hero-badge-icon' }
    },
    {
      id: badgeTextID,
      builderId: `builder-${uuidv4()}`,
      type: 'span',
      name: 'Badge Text',
      parentId: badgeID,
      hierarchyName: getHierarchyName('span'),
      className: heroBadgeTextClass,
      styles: {},
      text: 'ADELFOX.DESIGN',
      attributes: { 'data-element': 'hero-badge-text' }
    },
    // Title "Bring all your work together"
    {
      id: titleID,
      builderId: `builder-${uuidv4()}`,
      type: 'h1',
      name: 'Hero Title',
      parentId: leftColID,
      hierarchyName: getHierarchyName('h1'),
      className: heroTitleClass,
      styles: {},
      children: [titleIconID], // Icon will be appended to text by rendering logic or styled as pseudo-element
      text: 'Bring all your work together',
      attributes: { 'data-element': 'hero-title' }
    },
    // Lightning Bolt Icon
    {
        id: titleIconID,
        builderId: `builder-${uuidv4()}`,
        type: 'span', // Using span for inline icon, could be SVG
        name: 'Title Icon',
        parentId: titleID, // This might need adjustment based on how you handle children vs text
        hierarchyName: getHierarchyName('span'),
        className: heroTitleIconClass,
        styles: {}, // SVG content will be text
        text: '⚡️', // Actual lightning emoji or SVG path
        attributes: { 'data-element': 'hero-title-icon', 'aria-hidden': 'true' }
    },
    // Subtitle
    {
      id: subtitleID,
      builderId: `builder-${uuidv4()}`,
      type: 'p',
      name: 'Hero Subtitle',
      parentId: leftColID,
      hierarchyName: getHierarchyName('p'),
      className: heroSubtitleClass,
      styles: {},
      children: [],
      text: 'A better experience for your attendees and less stress for your team.',
      attributes: { 'data-element': 'hero-subtitle' }
    },
    // Button "Download for Free"
    {
      id: buttonID,
      builderId: `builder-${uuidv4()}`,
      type: 'button',
      name: 'Hero Button',
      parentId: leftColID,
      hierarchyName: getHierarchyName('button'),
      className: heroButtonClass,
      styles: {},
      children: [],
      text: 'Download for Free',
      attributes: { 'data-element': 'hero-button' }
    },
    // Scroll Down Icon
    {
      id: scrollDownID,
      builderId: `builder-${uuidv4()}`,
      type: 'div', // Will use an SVG or an icon font
      name: 'Scroll Down Hint',
      parentId: sectionID, // Child of section, positioned absolutely
      hierarchyName: getHierarchyName('div'),
      className: heroScrollDownClass,
      styles: { fontSize: '24px', color: '#0B0F19' }, // Basic styling for emoji
      text: '↓', // Simple arrow, replace with SVG for better visuals
      attributes: { 'data-element': 'hero-scroll-down', 'aria-label': 'Scroll down' }
    },

    // Right Column
    {
      id: rightColID,
      builderId: `builder-${uuidv4()}`,
      type: 'div',
      name: 'Right Column',
      parentId: containerID,
      hierarchyName: getHierarchyName('div'),
      className: heroRightColClass,
      styles: {},
      children: [imageWrapperID],
      text: '',
      attributes: { 'data-element': 'hero-right-column' }
    },
    // Image Wrapper (for main image and abstract shapes)
    {
      id: imageWrapperID,
      builderId: `builder-${uuidv4()}`,
      type: 'div',
      name: 'Image Wrapper',
      parentId: rightColID,
      hierarchyName: getHierarchyName('div'),
      className: heroImageWrapperClass,
      styles: {},
      children: [abstractShape1ID, abstractShape2ID, abstractShape3ID, abstractShape4ID, mainImageID, chat1ID, chat2ID, chat3ID, chat4ID],
      text: '',
      attributes: { 'data-element': 'hero-image-wrapper' }
    },
    // Abstract Shapes
    { id: abstractShape1ID, builderId: `builder-${uuidv4()}`, type: 'div', name: 'Abstract Shape 1', parentId: imageWrapperID, hierarchyName: getHierarchyName('div'), className: heroAbstractShape1Class, styles: {}, children: [], text: '', attributes: {'data-element': 'abstract-shape'} },
    { id: abstractShape2ID, builderId: `builder-${uuidv4()}`, type: 'div', name: 'Abstract Shape 2', parentId: imageWrapperID, hierarchyName: getHierarchyName('div'), className: heroAbstractShape2Class, styles: {}, children: [], text: '', attributes: {'data-element': 'abstract-shape'} },
    { id: abstractShape3ID, builderId: `builder-${uuidv4()}`, type: 'div', name: 'Abstract Shape 3', parentId: imageWrapperID, hierarchyName: getHierarchyName('div'), className: heroAbstractShape3Class, styles: {}, children: [], text: '', attributes: {'data-element': 'abstract-shape'} },
    { 
      id: abstractShape4ID, builderId: `builder-${uuidv4()}`, type: 'div', name: 'Abstract Shape 4 (Squiggle)', parentId: imageWrapperID, hierarchyName: getHierarchyName('div'), className: heroAbstractShape4Class, 
      // For SVG squiggle, you might embed SVG directly in text or use an <img> with src
      // styles: { width: '80px', height: '40px', fill: 'none', stroke: '#0B0F19', strokeWidth: '3' }, // Example for inline SVG
      // text: '<svg viewBox="0 0 100 50"><path d="M 5 25 Q 25 5, 45 25 T 85 25" /></svg>',
      styles: { 
        width: '60px', height: '30px', 
        // Using a simple CSS squiggle for now
        border: '3px solid #0B0F19',
        borderRadius: '50% / 0 0 100% 100%',
        borderBottom: 'none',
        borderRight: 'none',
        transform: 'rotate(-15deg)'
      },
      children: [], text: '', 
      attributes: {'data-element': 'abstract-shape-squiggle'} 
    },
    // Main Image
    {
      id: mainImageID,
      builderId: `builder-${uuidv4()}`,
      type: 'image',
      name: 'Hero Main Image',
      parentId: imageWrapperID,
      hierarchyName: getHierarchyName('image'),
      className: heroMainImageClass,
      styles: {},
      children: [],
      text: '',
      src: 'https://images.unsplash.com/photo-1581090464777-f3220bbe1b8b?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60', // Placeholder, replace with actual image URL
      attributes: { 'data-element': 'hero-main-image', alt: 'Person working' }
    },
    // Floating UI Elements
    // Chat 1 (Top left of image)
    {
      id: chat1ID,
      builderId: `builder-${uuidv4()}`,
      type: 'div',
      name: 'Floating Chat 1',
      parentId: imageWrapperID,
      hierarchyName: getHierarchyName('div'),
      className: heroFloatingUIChat1Class,
      styles: {},
      children: [chat1AvatarID, chat1ContentID], // Avatar and content (lines, icon)
      text: '',
      attributes: { 'data-element': 'floating-ui' }
    },
    {
      id: chat1AvatarID, builderId: `builder-${uuidv4()}`, type: 'image', name: 'Chat 1 Avatar', parentId: chat1ID, hierarchyName: getHierarchyName('image'), className: heroAvatarClass, src: 'https://randomuser.me/api/portraits/women/68.jpg', styles: {}, children: [], text: '', attributes: {'data-element': 'avatar', alt:'User avatar'}
    },
    {
      id: chat1ContentID, builderId: `builder-${uuidv4()}`, type: 'div', name: 'Chat 1 Content', parentId: chat1ID, hierarchyName: getHierarchyName('div'), styles: { display: 'flex', flexDirection: 'column', gap: '4px', flexGrow: 1}, 
      children: [
        chat1Line1ID, 
        chat1Line2ID, 
        chat1CallIconID 
      ], 
      text: '', attributes: {'data-element': 'chat-content'}
    },
     // Children for Chat 1 Content (lines and icon) - these need specific types and content
     {
      id: chat1Line1ID, builderId: `builder-${uuidv4()}`, type: 'div', name: 'Chat Line 1', parentId: chat1ContentID, hierarchyName: getHierarchyName('div'), styles: { width: '70%', height: '8px', backgroundColor: '#CBD5E0', borderRadius: '4px'}, children: [], text: '', attributes: {}
    },
    {
      id: chat1Line2ID, builderId: `builder-${uuidv4()}`, type: 'div', name: 'Chat Line 2', parentId: chat1ContentID, hierarchyName: getHierarchyName('div'), styles: { width: '50%', height: '8px', backgroundColor: '#CBD5E0', borderRadius: '4px'}, children: [], text: '', attributes: {}
    },
    {
      id: chat1CallIconID, builderId: `builder-${uuidv4()}`, type: 'span', name: 'Call Icon', parentId: chat1ContentID, hierarchyName: getHierarchyName('span'), styles: { color: '#34D399', fontSize: '20px', marginLeft: 'auto' }, text: '📞', attributes: {'aria-label': 'Call'} // Phone emoji
    },

    // Chat 2 "Mireya" (Orange)
    {
      id: chat2ID,
      builderId: `builder-${uuidv4()}`,
      type: 'div',
      name: 'Floating Chat 2',
      parentId: imageWrapperID,
      hierarchyName: getHierarchyName('div'),
      className: heroFloatingUIChat2Class,
      styles: {},
      children: [],
      text: 'Mireya',
      attributes: { 'data-element': 'floating-ui-namecard' }
    },
    // Chat 3 (Bottom right of image with progress)
    {
      id: chat3ID,
      builderId: `builder-${uuidv4()}`,
      type: 'div',
      name: 'Floating Chat 3',
      parentId: imageWrapperID,
      hierarchyName: getHierarchyName('div'),
      className: heroFloatingUIChat3Class,
      styles: {},
      children: [chat3AvatarID, chat3ContentID],
      text: '',
      attributes: { 'data-element': 'floating-ui' }
    },
    {
      id: chat3AvatarID, builderId: `builder-${uuidv4()}`, type: 'image', name: 'Chat 3 Avatar', parentId: chat3ID, hierarchyName: getHierarchyName('image'), className: heroAvatarClass, src: 'https://randomuser.me/api/portraits/men/32.jpg', styles: {}, children: [], text: '', attributes: {'data-element': 'avatar', alt:'User avatar'}
    },
    {
      id: chat3ContentID, builderId: `builder-${uuidv4()}`, type: 'div', name: 'Chat 3 Content', parentId: chat3ID, hierarchyName: getHierarchyName('div'), styles: { display: 'flex', flexDirection: 'column', gap: '6px', flexGrow: 1 }, 
      children: [chat3CheckmarkID, chat3ProgressWrapperID], 
      text: '', attributes: {'data-element': 'chat-content-progress'}
    },
    // Children for Chat 3 Content (checkmark, progress bar)
    {
      id: chat3CheckmarkID, builderId: `builder-${uuidv4()}`, type: 'span', name: 'Checkmark Icon', parentId: chat3ContentID, hierarchyName: getHierarchyName('span'), styles: { color: '#34D399', fontSize: '16px' }, text: '✔️', attributes: {'aria-label': 'Completed'}
    },
    { // Progress bar wrapper
      id: chat3ProgressWrapperID, builderId: `builder-${uuidv4()}`, type: 'div', name: 'Progress Bar Wrapper', parentId: chat3ContentID, hierarchyName: getHierarchyName('div'), 
      styles: { width: '100%', height: '10px', backgroundColor: '#E2E8F0', borderRadius: '5px', overflow: 'hidden', position:'relative' }, 
      children: [chat3ProgressFillID], 
      text: '', attributes: {}
    },
    { // Actual progress fill
      id: chat3ProgressFillID, builderId: `builder-${uuidv4()}`, type: 'div', name: 'Progress Fill', parentId: chat3ProgressWrapperID, hierarchyName: getHierarchyName('div'), 
      styles: { width: '75%', height: '100%', backgroundColor: '#3B82F6', borderRadius: '5px' }, 
      children: [], text: '', attributes: {}
    },


    // Chat 4 "Davin" (Purple, partially visible, might be simpler)
    {
      id: chat4ID,
      builderId: `builder-${uuidv4()}`,
      type: 'div',
      name: 'Floating Chat 4',
      parentId: imageWrapperID,
      hierarchyName: getHierarchyName('div'),
      className: heroFloatingUIChat4Class,
      styles: {},
      children: [],
      text: 'Davin',
      attributes: { 'data-element': 'floating-ui-namecard' }
    },
  ];
  
  return {
    heroSection: newElements.find(el => el.type === 'section'),
    elements: newElements
  };
};

export default createHeroElements; 